import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback } from "react";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { Task } from "@common/types/task";
import { trackEvent } from "@lib/analytics";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useTaskListOpenTask } from "@components/tasks/taskList.context";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { HStack } from "@design-system/HStack/HStack";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { NestedTaskRow } from "../../types";
import { useOpenTaskSection } from "../../useOpenTaskSection";
import { TaskChatButton } from "../../TaskChatButton";
import { TaskActionMenuItems, useTaskActions } from "../../useTaskActions";

export const ActionsCell = memo(
    ({ row }: CellContext<ViewTableFeatures, NestedTaskRow, unknown>) => {
        const task = row.original as Task;
        const openTask = useTaskListOpenTask();

        const unreadCount = row.original.unreadCount ?? 0;
        const hasNewMessages = unreadCount > 0;

        const openChat = useOpenTaskSection(openTask, "chat");
        const openDetails = useOpenTaskSection(openTask, "details");
        const { items, dialogs } = useTaskActions({
            task,
            onEditDetails: openDetails,
        });

        const handleChatClick = useCallback(() => {
            void trackEvent(AnalyticsEvent.TASK_LIST_CHAT_CLICKED, {
                taskId: task.id,
                taskType: task.type,
                unreadCount,
            });
            openChat(task);
        }, [openChat, task, unreadCount]);

        if (
            row.original.isGroup ||
            row.original.tasks?.length > 0 ||
            row.original.isNewPlaceholder
        ) {
            return null;
        }

        return (
            <ViewTableCell captureClicks>
                <HStack justify="end" gap={12} className="w-100">
                    <TaskChatButton
                        unreadCount={unreadCount}
                        label="Open task chat"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleChatClick();
                        }}
                        className={
                            hasNewMessages ? undefined : tableStyles.showOnHover
                        }
                    />

                    <Menu.More className={tableStyles.showOnHover}>
                        <TaskActionMenuItems items={items} />
                    </Menu.More>

                    {dialogs}
                </HStack>
            </ViewTableCell>
        );
    },
);
